import { CognitoUser } from "@aws-amplify/auth";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import { Auth } from "aws-amplify";
import { PredictiveAddressSearch } from "components/organisations/pav-address-input";
import { ViewEditInput } from "components/shared/view-edit-input";
import { workDescription } from "components/user/sign-in/states";
import { IdentityState, useIdentity, User } from "contexts/identity-context";
import * as QueryKeys from "data";
import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useDarkMode from "use-dark-mode";
import { resolveIdentityId } from "utils/identity";
import { object, string } from "yup";
import { MfaMethodType } from "../security/user-security";
import { VerifyModal } from "./verify-modal";

export const UserProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingPreferences, setIsEditingPreferences] = useState(false);

  const [identity, setIdentity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const user = useRef<User>();
  const [cognitoUser, setCognitoUser] = useState<CognitoUser>(
    (identity as User).cognitoUser,
  );

  useEffect(() => {
    // Only copy once on first mount
    if (!user.current && identity && identity !== "loading") {
      user.current = { ...(identity as User) };
    }
  }, []);

  const authenticatedUserQuery = useQuery({
    queryKey: [QueryKeys.authenticatedUser, identityId],
    queryFn: () =>
      Auth.currentAuthenticatedUser({
        bypassCache: true,
      }),
    enabled: !isEditing,
    refetchOnWindowFocus: !isEditing,
    refetchOnMount: !isEditing,
    refetchOnReconnect: !isEditing,
  });

  useEffect(() => {
    if (authenticatedUserQuery.isSuccess && authenticatedUserQuery.data) {
      setCognitoUser(authenticatedUserQuery.data);
      const dynamicUser = authenticatedUserQuery.data as any;
      user.current = {
        ...user.current!,
        firstName: dynamicUser.attributes.given_name,
        lastName: dynamicUser.attributes.family_name,
        email: dynamicUser.attributes.email,
        phoneNumber: dynamicUser.attributes.phone_number,
        role: dynamicUser.attributes["custom:role"],
        workPhone: dynamicUser.attributes["custom:work_phone"],
        phoneNumberVerified: dynamicUser.attributes.phone_number_verified,
        emailVerified: dynamicUser.attributes.email_verified,
        address: dynamicUser.attributes.address,
      };
    }
  }, [authenticatedUserQuery.isSuccess]);

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const darkMode = useDarkMode(false);

  type Mode = "Dark" | "Light" | "System";
  const [mode, setMode] = useState<Mode>(isDark ? "Dark" : "Light");
  const [submitError, setSubmitError] = useState("");
  const [phoneVerifyOpen, setPhoneVerifyOpen] = useState(false);
  const [emailVerifyOpen, setEmailVerifyOpen] = useState(false);

  const [isPhoneUpdateLoading, setIsPhoneUpdateLoading] = useState(false);
  const [isEmailUpdateLoading, setIsEmailUpdateLoading] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);

  const handlePhoneUpdate = async (phoneNumber: string) => {
    setIsPhoneUpdateLoading(true);
    await Auth.updateUserAttributes(cognitoUser, {
      "phone_number": phoneNumber,
    }).finally(() => {
      setIsPhoneUpdateLoading(false);
      setPhoneVerifyOpen(true);
    });
  };

  const handleEmailUpdate = async (email: string) => {
    setIsEmailUpdateLoading(true);
    await Auth.updateUserAttributes(cognitoUser, {
      "email": email,
    }).finally(() => {
      setIsEmailUpdateLoading(false);
      setEmailVerifyOpen(true);
    });
  };

  const handleEmailVerificationSuccess = (email: string) => {
    setIdentity((prevIdentity: IdentityState) => {
      if (prevIdentity && prevIdentity !== "loading") {
        return {
          ...prevIdentity,
          email: email,
          emailVerified: true,
        };
      }
      return prevIdentity;
    });
    user.current = {
      ...user.current!,
      email: email,
      emailVerified: true,
    };
  };

  const handlePhoneVerificationSuccess = (phone: string) => {
    setIdentity((prevIdentity: IdentityState) => {
      if (prevIdentity && prevIdentity !== "loading") {
        return {
          ...prevIdentity,
          phoneNumber: phone,
          emailVerified: true,
        };
      }
      return prevIdentity;
    });
    user.current = {
      ...user.current!,
      phoneNumber: phone,
      phoneNumberVerified: true,
    };
  };

  const validationSchema = object().shape({
    firstName: string()
      .matches(
        /^[a-zA-Z\s'-]+$/,
        "First name can only contain letters, spaces, hyphens, and apostrophes",
      )
      .max(256, "First name must be at most 256 characters")
      .required("First name is required"),
    lastName: string()
      .matches(
        /^[a-zA-Z\s'-]+$/,
        "Last name can only contain letters, spaces, hyphens, and apostrophes",
      )
      .max(256, "Last name must be at most 256 characters")
      .required("Last name is required"),
    phoneNumber: string()
      .when([], {
        is: () => mfaEnabled,
        then: (schema) =>
          schema.required("Phone number is required when MFA is enabled"),
        otherwise: (schema) => schema.notRequired(),
      })
      .matches(
        /^\+61\d{9}$/,
        "Phone number must be in the format +61XXXXXXXXX",
      ),
    workPhone: string().matches(
      /^\+61\d{9}$/,
      "Work phone number must be in the format +61XXXXXXXXX",
    ),
    email: string()
      .max(256, "Email must be at most 256 characters")
      .email("Invalid email address")
      .required("Email is required"),
    address: string().nullable(true),
  });

  const handleThemeChange = () => {
    if (mode == "Dark") {
      darkMode.enable();
    } else if (mode == "Light") {
      darkMode.disable();
    } else if (mode == "System") {
      const isDark = window.matchMedia("(prefers-color-scheme: dark)");
      if (isDark.matches) {
        darkMode.enable();
      } else {
        darkMode.disable();
      }
    }
  };

  const resetTheme = () => {
    setMode(isDark ? "Dark" : "Light");
  };

  const submitPreferences = () => {
    handleThemeChange();
    setIsEditingPreferences(false);
  };

  const cancelPreferences = () => {
    resetTheme();
  };

  const preferredMfaQuery = useQuery({
    queryKey: [QueryKeys.preferredMfa, identityId],
    queryFn: () =>
      Auth.getPreferredMFA(cognitoUser, {
        bypassCache: true,
      }),
    enabled: !!cognitoUser,
  });

  useEffect(() => {
    if (preferredMfaQuery.isSuccess && preferredMfaQuery.data) {
      setMfaEnabled(preferredMfaQuery.data !== "NOMFA");
    }
  }, [preferredMfaQuery.isSuccess]);

  const updateUserProfile = async (hubUser: User) => {
    await Auth.updateUserAttributes(cognitoUser, {
      "given_name": hubUser.firstName,
      "family_name": hubUser.lastName,
      "phone_number": hubUser.phoneNumber ?? "",
      "email": hubUser.email,
      "custom:work_phone": hubUser.workPhone ?? "",
      "custom:role": hubUser.role ?? "",
      "address": hubUser.address ?? "",
    })
      .then(() => {
        setSubmitError("");
        setIdentity((prevIdentity: IdentityState) => {
          if (prevIdentity && prevIdentity !== "loading") {
            return {
              ...prevIdentity,
              firstName: hubUser.firstName,
              lastName: hubUser.lastName,
              email: hubUser.email,
              phoneNumber: hubUser.phoneNumber,
              role: hubUser.role,
              workPhone: hubUser.workPhone,
              phoneNumberVerified: (cognitoUser as any).attributes
                .phone_number_verified,
              emailVerified: (cognitoUser as any).attributes.email_verified,
              address: hubUser.address,
            };
          }
          return prevIdentity;
        });
        user.current = hubUser;
      })
      .catch((e) => {
        setSubmitError(e.message || "Something went wrong");
        throw new Error(e.message || "Something went wrong");
      })
      .finally(() => {
        setIsEditing(false);
        authenticatedUserQuery.refetch();
      });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <CardHeader title="User Profile" />
      </Grid>
      {authenticatedUserQuery.isEnabled && authenticatedUserQuery.isPending && (
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "408px" }}>
            <CardContent>
              <Skeleton variant="rectangular" height="355px" />
            </CardContent>
          </Card>
        </Grid>
      )}
      {authenticatedUserQuery.isSuccess && user.current && (
        <Grid item xs={12} md={6}>
          <Formik<User>
            enableReinitialize={true}
            initialValues={{
              ...user.current,
            }}
            onSubmit={async (values, { resetForm }) => {
              await updateUserProfile(values).catch(resetForm);
            }}
            validationSchema={validationSchema}
          >
            {({
              values,
              handleChange,
              isSubmitting,
              setFieldValue,
              handleBlur,
              errors,
              touched,
              resetForm,
              isValid,
              dirty,
            }: any) => (
              <Form>
                <Card>
                  <CardContent>
                    <Box
                      gap={2}
                      display="flex"
                      flexDirection="column"
                      marginBottom={isEditing ? 0 : 1}
                    >
                      {submitError && (
                        <Alert severity="error" variant="outlined">
                          {submitError}
                        </Alert>
                      )}
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <ViewEditInput
                            isEditing={isEditing}
                            style={{ minWidth: "368px" }}
                          >
                            <TextField
                              id="firstName"
                              name="firstName"
                              label="First Name"
                              type="text"
                              margin="normal"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                touched.firstName && Boolean(errors.firstName)
                              }
                              value={values.firstName}
                              variant="outlined"
                              fullWidth
                              size="small"
                              helperText={touched.firstName && errors.firstName}
                              inputProps={{ maxLength: 256 }}
                              sx={{
                                padding: "0px",
                                margin: "0px",
                                marginTop: 1,
                              }}
                            />
                          </ViewEditInput>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <ViewEditInput isEditing={isEditing}>
                            <TextField
                              id="lastName"
                              name="lastName"
                              label="Last Name"
                              type="text"
                              margin="normal"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                touched.lastName && Boolean(errors.lastName)
                              }
                              value={values.lastName}
                              variant="outlined"
                              fullWidth
                              size="small"
                              helperText={touched.lastName && errors.lastName}
                              inputProps={{ maxLength: 256 }}
                              sx={{
                                padding: "0px",
                                margin: "0px",
                                marginTop: 1,
                              }}
                            />
                          </ViewEditInput>
                        </Grid>
                      </Grid>
                      <ViewEditInput
                        isEditing={isEditing}
                        label="Role"
                        value={values.role}
                      >
                        <FormControl fullWidth>
                          <InputLabel id="user-role-label">Role</InputLabel>
                          <Select
                            disabled={!isEditing}
                            fullWidth
                            size="small"
                            label="Role"
                            labelId="user-role-label"
                            id="role"
                            value={values.role}
                            onChange={(event: SelectChangeEvent) => {
                              setFieldValue("role", event.target.value);
                            }}
                            input={<OutlinedInput label="Role" />}
                          >
                            {workDescription
                              .sort((a, b) => a.localeCompare(b))
                              .map((service, index) => (
                                <MenuItem key={index} value={service}>
                                  {service}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </ViewEditInput>
                      <Grid item xs={12} md={12}>
                        <ViewEditInput
                          isEditing={isEditing}
                          value={values.email}
                          label="Email"
                        >
                          <Stack direction="column" spacing={1}>
                            <Stack direction="row" spacing={1}>
                              <TextField
                                id="email"
                                name="email"
                                label="Email"
                                type="email"
                                margin="normal"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.email && Boolean(errors.email)}
                                value={values.email}
                                variant="outlined"
                                fullWidth
                                size="small"
                                inputProps={{ maxLength: 256 }}
                                helperText={touched.email && errors.email}
                                sx={{
                                  padding: "0px",
                                  margin: "0px",
                                  marginTop: isEditing ? 0 : 1,
                                }}
                              />
                              <Tooltip title="Please verify your email before saving">
                                <LoadingButton
                                  size="small"
                                  variant="contained"
                                  loading={isEmailUpdateLoading}
                                  onClick={async () => {
                                    handleEmailUpdate(values.email);
                                  }}
                                  disabled={
                                    errors.email ||
                                    (values.emailVerified &&
                                      values.email == user.current!.email)
                                  }
                                >
                                  Verify
                                </LoadingButton>
                              </Tooltip>
                            </Stack>
                            <Alert variant="outlined" severity="info">
                              Changing your email address will also update your
                              login credentials. You’ll use your new email
                              address to sign in.
                            </Alert>
                          </Stack>
                        </ViewEditInput>
                      </Grid>
                      <ViewEditInput isEditing={isEditing}>
                        <PredictiveAddressSearch
                          disabled={!isEditing}
                          readOnly={!isEditing}
                          textFieldStyle={{
                            marginTop: "0px",
                            marginBottom: "0px",
                          }}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          id="address"
                          size="small"
                          name="address"
                          label="Address"
                          value={values.address}
                          error={
                            touched.address !== null &&
                            touched.address &&
                            Boolean(errors.address)
                          }
                          helperText={touched.address && errors.address}
                        />
                      </ViewEditInput>
                      <ViewEditInput isEditing={isEditing}>
                        <TextField
                          id="workPhone"
                          size="small"
                          label="Work"
                          placeholder="+61xxxxxxxxx"
                          name="workPhone"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.workPhone}
                          error={touched.workPhone && Boolean(errors.workPhone)}
                          helperText={touched.workPhone && errors.workPhone}
                        />
                      </ViewEditInput>
                      <Grid item xs={12} md={12}>
                        <ViewEditInput
                          isEditing={isEditing}
                          value={values.phoneNumber}
                          label="Mobile"
                        >
                          <Stack direction="row" spacing={1}>
                            <TextField
                              id="phoneNumber"
                              size="small"
                              label="Mobile"
                              name="phoneNumber"
                              placeholder="+61xxxxxxxxx"
                              fullWidth
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.phoneNumber}
                              error={
                                touched.phoneNumber &&
                                Boolean(errors.phoneNumber)
                              }
                              helperText={
                                touched.phoneNumber && errors.phoneNumber
                              }
                            />
                            <Tooltip title="Please verify your phone number before saving">
                              <LoadingButton
                                loading={isPhoneUpdateLoading}
                                variant="contained"
                                size="small"
                                onClick={async () => {
                                  handlePhoneUpdate(values.phoneNumber);
                                }}
                                disabled={
                                  errors.phoneNumber ||
                                  !values.phoneNumber ||
                                  (values.phoneNumberVerified &&
                                    values.phoneNumber ==
                                      user.current!.phoneNumber)
                                }
                              >
                                Verify
                              </LoadingButton>
                            </Tooltip>
                          </Stack>
                        </ViewEditInput>
                      </Grid>
                    </Box>
                  </CardContent>
                  <CardActions
                    sx={{
                      justifyContent: "flex-end",
                    }}
                  >
                    {!isEditing && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setIsEditing(true)}
                      >
                        Edit
                      </Button>
                    )}
                    {isEditing && (
                      <Box display="flex" flexDirection="row" gap={2}>
                        <Button
                          size="small"
                          onClick={() => {
                            resetForm();
                            setIsEditing(false);
                            setSubmitError("");
                          }}
                        >
                          Cancel
                        </Button>
                        <LoadingButton
                          loading={isSubmitting}
                          size="small"
                          variant="contained"
                          type="submit"
                          disabled={
                            !isValid ||
                            !dirty ||
                            (values.phoneNumber &&
                              values.phoneNumber !==
                                user.current!.phoneNumber) ||
                            values.email !== user.current!.email
                          }
                        >
                          Save
                        </LoadingButton>
                      </Box>
                    )}
                  </CardActions>
                </Card>

                <VerifyModal
                  isCodeSent={true}
                  open={emailVerifyOpen}
                  title="Verify your email"
                  subheader={`We've sent an email with a verification code to your email ${values.email}`}
                  attribute="email"
                  onClose={() => {
                    setEmailVerifyOpen(false);
                    authenticatedUserQuery.refetch();
                  }}
                  onSuccess={() => handleEmailVerificationSuccess(values.email)}
                />
                <VerifyModal
                  isCodeSent={true}
                  open={phoneVerifyOpen}
                  title="Verify your phone number"
                  subheader={`We've sent an SMS with a verification code to your phone ${values.phoneNumber}`}
                  attribute="phone_number"
                  onClose={() => {
                    setPhoneVerifyOpen(false);
                    authenticatedUserQuery.refetch();
                  }}
                  onSuccess={() =>
                    handlePhoneVerificationSuccess(values.phoneNumber)
                  }
                />
              </Form>
            )}
          </Formik>
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <CardHeader title="Preferences" />
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box
              gap={2}
              display="flex"
              flexDirection="column"
              marginBottom={isEditingPreferences ? 0 : 1}
            >
              <ViewEditInput
                isEditing={isEditingPreferences}
                label="Theme"
                value={mode}
              >
                <FormControl
                  fullWidth
                  sx={{ padding: "0px", margin: "0px", marginTop: 1 }}
                >
                  <InputLabel id="user-theme-label">Theme</InputLabel>
                  <Select
                    disabled={!isEditingPreferences}
                    fullWidth
                    size="small"
                    label="Theme"
                    labelId="user-theme-label"
                    id="theme-multi-select"
                    value={mode}
                    onChange={(event: SelectChangeEvent) => {
                      setMode(event.target.value as Mode);
                    }}
                    input={<OutlinedInput label="Theme" />}
                  >
                    <MenuItem value="Light">Light</MenuItem>
                    <MenuItem value="Dark">Dark</MenuItem>
                    <MenuItem value="System">System</MenuItem>
                  </Select>
                </FormControl>
              </ViewEditInput>
              <ViewEditInput isEditing={isEditingPreferences}>
                <TextField
                  id="language"
                  name="language"
                  label="Language"
                  type="text"
                  value="English (UK)"
                  variant="outlined"
                  fullWidth
                  disabled
                  size="small"
                />
              </ViewEditInput>
              <ViewEditInput isEditing={isEditingPreferences}>
                <TextField
                  id="timezone"
                  name="timezone"
                  label="Time Zone"
                  type="text"
                  value="Australia/Sydney (+11:00)"
                  variant="outlined"
                  fullWidth
                  disabled
                  size="small"
                />
              </ViewEditInput>
            </Box>
          </CardContent>
          <CardActions
            sx={{
              justifyContent: "flex-end",
            }}
          >
            {!isEditingPreferences && (
              <Button
                size="small"
                variant="contained"
                onClick={() => setIsEditingPreferences(true)}
              >
                Edit
              </Button>
            )}
            {isEditingPreferences && (
              <Box display="flex" flexDirection="row" gap={2}>
                <Button
                  size="small"
                  onClick={() => {
                    cancelPreferences();
                    setIsEditingPreferences(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={submitPreferences}
                >
                  Save
                </Button>
              </Box>
            )}
          </CardActions>
        </Card>
      </Grid>
    </Grid>
  );
};
